import React, { useMemo } from 'react';
import { className } from 'design-system';
import {
    Layout,
    useTranslate,
    Head as DefaultHead,
} from 'gatsby-theme-soluble-source';

import HeroSection from '../components/HeroSection';
import PostMetaSection from '../components/PostMetaSection';
import PostContentSection from '../components/PostContentSection';
import ServicesBoxSection from '../components/ServicesBoxSection';
import CaseStudiesSection from '../components/CaseStudiesSection';
import ArticlesSection from '../components/ArticlesSection';
import NewsSection from '../components/NewsSection';

function formatServiceSector(servicesSectors, type) {
    if (!servicesSectors?.length) {
        return [];
    }

    return servicesSectors
        .filter((ss) => ss.__typename === type && !!ss.page && !ss?.hide)
        .map((ss) => ({
            ...ss,
            slug: ss.page.slug,
        }));
}

export function Head({ data, ...props }) {
    const customSeo = useMemo(() => {
        let output = {};

        if (!data.post.seo?.title) {
            output.title = data.post.title;
        }
        if (data.post.excerpt) {
            output.description = data.post.excerpt;
        }
        if (!data.post.seo?.image && data.post.seoImage?.publicURL) {
            output.image = data.post.seoImage;
        }

        return output;
    }, [data.post]);

    return <DefaultHead data={data} customSeo={customSeo} {...props} />;
}

export default function Post({ data, pageContext }) {
    const translation = useTranslate();

    const [services, categories, sectors, relatedServices, teamMembers] =
        useMemo(() => {
            let sectors = formatServiceSector(
                data.post.servicesSectors,
                'DatoCmsSector',
            );
            let categories = formatServiceSector(
                data.post.servicesSectors,
                'DatoCmsCategory',
            );

            let filteredServices = {};

            data.post.services.forEach((service) => {
                filteredServices[service.title] = {
                    ...service,
                    slug: service.page.slug,
                };
            });

            // Remove duplicate TeamMembers
            const buTm = data.post.buTeamMember;
            const serviceTm = data.post.serviceTeamMember;
            let teamMembers = [buTm]
                .concat(serviceTm?.email !== buTm?.email ? [serviceTm] : [])
                .filter((i) => !!i);

            if (data?.post?.customMade && data?.post?.authors?.length > 0) {
                teamMembers = data?.post?.authors ?? [];
            }

            return [
                Object.values(filteredServices).filter(
                    (s) => !!s?.slug && !s?.hide,
                ),
                categories,
                sectors,
                sectors?.length
                    ? sectors[0].relatedServices.map((rs) => ({
                        ...rs,
                        slug: rs.page.slug,
                    }))
                    : [],
                teamMembers,
            ];
        }, [data.post]);

    const metas = useMemo(() => {
        return !data?.post?.customMade
            ? `${translation['post.date.author'].text.replace(
                '{author}',
                data.post.authors?.length
                    ? data.post.authors.map((a) => a.name).join(', ')
                    : 'Nae',
            )} — ${data.post.meta.firstPublishedAt}`
            : '';
    }, [data.post, translation]);

    const articlesRelatedTitle = useMemo(
        () =>
            translation?.['post.related.articles.pretitle']?.text?.replace(
                `:service`,
                data?.post?.services?.length ? data.post.services[0].title : '',
            ),
        [data],
    );

    return (
        <Layout
            data-navbar-child-height
            headerProps={{
                forceTransparent: true,
            }}>
            {data.post ? (
                <>
                    <HeroSection
                        pretitle={
                            !data?.post?.customMade ? data.categoryName : null
                        }
                        title={data.post.title}
                        titleWider={true}
                        type="small"
                        image={data.post.featuredImage}
                        imageType="none-right"
                        light={true}
                        aspectRatio={1.27}
                    />
                    <PostMetaSection
                        metas={metas}
                        url={pageContext.pageUrl}
                        title={data.post.title}
                    />
                    <PostContentSection
                        content={data.post.content}
                        sectors={sectors}
                        services={services}
                        categories={categories}
                        teamMembers={teamMembers}
                    />
                    {relatedServices?.length ? (
                        <ServicesBoxSection
                            wrapperClassName="mt-10 mb-20"
                            title={translation['post.related.title'].text}
                            serviceChilds={relatedServices}
                        />
                    ) : null}
                    {data?.post?.relatedCaseOfStudy?.length ? (
                        <CaseStudiesSection
                            posts={data.post.relatedCaseOfStudy}
                        />
                    ) : data?.post?.relatedPosts?.length ? (
                        <NewsSection
                            pt={{ xs: 0 }}
                            title={translation['post.related.news'].text}
                            posts={data?.post.relatedPosts}
                        />
                    ) : data?.post?.relatedArticles?.length ? (
                        <ArticlesSection
                            title={
                                translation['post.related.articles.title'].text
                            }
                            pretitle={articlesRelatedTitle}
                            pt={{ xs: 0 }}
                            posts={data.post.relatedArticles}
                        />
                    ) : (
                        <div {...className('pt-lg-10')} />
                    )}
                </>
            ) : null}
        </Layout>
    );
}
